import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Eye, EyeOff, Calendar, Clock, MapPin, 
  Loader2, MessageSquare, User, ChevronDown, ChevronUp,
  FileText, Users
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const CATEGORIES = [
  { value: 'general', label: 'General', color: 'bg-stone-100 text-stone-700' },
  { value: 'language', label: 'Language', color: 'bg-blue-100 text-blue-700' },
  { value: 'articulation', label: 'Articulation', color: 'bg-green-100 text-green-700' },
  { value: 'fluency', label: 'Fluency', color: 'bg-purple-100 text-purple-700' },
  { value: 'pragmatics', label: 'Pragmatics', color: 'bg-pink-100 text-pink-700' },
  { value: 'feeding', label: 'Feeding', color: 'bg-orange-100 text-orange-700' },
  { value: 'aac', label: 'AAC', color: 'bg-teal-100 text-teal-700' },
  { value: 'voice', label: 'Voice', color: 'bg-indigo-100 text-indigo-700' },
  { value: 'cognitive', label: 'Cognitive-Communication', color: 'bg-amber-100 text-amber-700' },
  { value: 'other', label: 'Other', color: 'bg-gray-100 text-gray-700' }
];

const SESSION_LOCATIONS = [
  { value: 'clinic', label: 'Clinic' },
  { value: 'home', label: 'Home Visit' },
  { value: 'school', label: 'School' },
  { value: 'teletherapy', label: 'Teletherapy' },
  { value: 'community', label: 'Community Setting' }
];

export default function SharedNotesSummary({ onLogout }) {
  const navigate = useNavigate();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedNotes, setExpandedNotes] = useState({});
  const [groupBy, setGroupBy] = useState('date'); // 'date' or 'child'

  useEffect(() => {
    fetchSharedNotes();
  }, []);

  const fetchSharedNotes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/therapist/shared-notes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotes(response.data);
    } catch (error) {
      toast.error('Failed to load shared notes');
    } finally {
      setLoading(false);
    }
  };

  const toggleShare = async (note) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/progress/${note.id}/share`,
        { 
          shared_with_parent: false,
          parent_summary: note.parent_summary || ''
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Note hidden from parent');
      fetchSharedNotes();
    } catch (error) {
      toast.error('Failed to update sharing');
    }
  };

  // Group notes by child
  const groupedByChild = notes.reduce((acc, note) => {
    const childName = note.child_name || 'Unknown';
    if (!acc[childName]) {
      acc[childName] = [];
    }
    acc[childName].push(note);
    return acc;
  }, {});

  // Group notes by month
  const groupedByMonth = notes.reduce((acc, note) => {
    const date = new Date(note.date);
    const monthKey = `${date.toLocaleString('default', { month: 'long' })} ${date.getFullYear()}`;
    if (!acc[monthKey]) {
      acc[monthKey] = [];
    }
    acc[monthKey].push(note);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Header */}
      <nav className="bg-white border-b border-stone-100 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/dashboard')} className="rounded-full">
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <h1 className="text-xl font-serif text-primary-900">Shared Notes Summary</h1>
              <p className="text-sm text-stone-500">Notes visible to parents</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant={groupBy === 'date' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setGroupBy('date')}
              className="rounded-full"
            >
              <Calendar className="w-4 h-4 mr-1" />
              By Date
            </Button>
            <Button
              variant={groupBy === 'child' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setGroupBy('child')}
              className="rounded-full"
            >
              <Users className="w-4 h-4 mr-1" />
              By Child
            </Button>
          </div>
        </div>
      </nav>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {notes.length === 0 ? (
          <Card className="p-12 text-center bg-white rounded-3xl border border-stone-100">
            <MessageSquare className="w-12 h-12 text-stone-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-stone-700 mb-2">No shared notes yet</h3>
            <p className="text-stone-500 mb-6">
              When you share session notes with parents, they'll appear here for easy tracking.
            </p>
            <Button onClick={() => navigate('/dashboard')} className="rounded-full">
              Go to Dashboard
            </Button>
          </Card>
        ) : (
          <div className="space-y-6">
            {/* Stats */}
            <Card className="p-4 bg-white rounded-2xl border border-stone-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                    <Eye className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-primary-900">{notes.length}</p>
                    <p className="text-sm text-stone-500">Shared Notes</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm text-stone-500">Across</p>
                  <p className="text-lg font-medium text-primary-900">
                    {Object.keys(groupedByChild).length} {Object.keys(groupedByChild).length === 1 ? 'child' : 'children'}
                  </p>
                </div>
              </div>
            </Card>

            {/* Notes grouped */}
            {groupBy === 'child' ? (
              Object.entries(groupedByChild).map(([childName, childNotes]) => (
                <div key={childName} className="space-y-3">
                  <div className="flex items-center gap-2">
                    <User className="w-5 h-5 text-primary" />
                    <h2 className="text-lg font-medium text-primary-900">{childName}</h2>
                    <Badge variant="secondary" className="ml-2">{childNotes.length} notes</Badge>
                  </div>
                  {childNotes.map((note) => (
                    <NoteCard
                      key={note.id}
                      note={note}
                      isExpanded={expandedNotes[note.id]}
                      onToggleExpand={() => setExpandedNotes(prev => ({ ...prev, [note.id]: !prev[note.id] }))}
                      onToggleShare={() => toggleShare(note)}
                      showChildName={false}
                    />
                  ))}
                </div>
              ))
            ) : (
              Object.entries(groupedByMonth).map(([month, monthNotes]) => (
                <div key={month} className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-primary" />
                    <h2 className="text-lg font-medium text-primary-900">{month}</h2>
                    <Badge variant="secondary" className="ml-2">{monthNotes.length} notes</Badge>
                  </div>
                  {monthNotes.map((note) => (
                    <NoteCard
                      key={note.id}
                      note={note}
                      isExpanded={expandedNotes[note.id]}
                      onToggleExpand={() => setExpandedNotes(prev => ({ ...prev, [note.id]: !prev[note.id] }))}
                      onToggleShare={() => toggleShare(note)}
                      showChildName={true}
                    />
                  ))}
                </div>
              ))
            )}
          </div>
        )}
      </main>
    </div>
  );
}

function NoteCard({ note, isExpanded, onToggleExpand, onToggleShare, showChildName }) {
  const noteCategories = note.categories || [note.category];
  
  return (
    <Card className="bg-white rounded-2xl border border-stone-100 overflow-hidden">
      <div
        className="p-4 cursor-pointer hover:bg-stone-50 transition-colors"
        onClick={onToggleExpand}
      >
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-4">
            <div className="text-center">
              <div className="w-12 h-12 rounded-xl bg-green-100 flex flex-col items-center justify-center">
                <span className="text-lg font-bold text-green-700">
                  {new Date(note.date).getDate()}
                </span>
                <span className="text-xs text-green-600">
                  {new Date(note.date).toLocaleDateString('en-US', { month: 'short' })}
                </span>
              </div>
            </div>
            <div className="flex-1">
              {showChildName && (
                <p className="text-sm font-medium text-primary-900 mb-1">{note.child_name}</p>
              )}
              <div className="flex items-center gap-2 flex-wrap mb-2">
                {noteCategories.map((cat, idx) => {
                  const catObj = CATEGORIES.find(c => c.value === cat) || CATEGORIES[0];
                  return (
                    <Badge key={idx} className={`${catObj.color} border-0 text-xs`}>
                      {catObj.label}
                    </Badge>
                  );
                })}
                <Badge variant="outline" className="text-xs">
                  <Clock className="w-3 h-3 mr-1" />
                  {note.session_duration || 30} min
                </Badge>
              </div>
              {note.parent_summary && (
                <p className="text-sm text-stone-600 line-clamp-2">
                  {note.parent_summary}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => { e.stopPropagation(); onToggleShare(); }}
              className="rounded-full text-red-500 hover:text-red-600 hover:bg-red-50"
              title="Hide from parent"
            >
              <EyeOff className="w-4 h-4" />
            </Button>
            {isExpanded ? <ChevronUp className="w-4 h-4 text-stone-400" /> : <ChevronDown className="w-4 h-4 text-stone-400" />}
          </div>
        </div>
      </div>
      
      {isExpanded && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          className="overflow-hidden"
        >
          <div className="px-4 pb-4 pt-0 border-t border-stone-100">
            <div className="mt-4 p-3 bg-blue-50 rounded-xl border border-blue-200">
              <p className="text-xs font-medium text-blue-700 mb-2 flex items-center gap-1">
                <MessageSquare className="w-3 h-3" /> What parents see:
              </p>
              <p className="text-sm text-stone-700">{note.parent_summary || 'No parent summary written'}</p>
            </div>
            
            {(note.objective || note.observation) && (
              <div className="mt-3 p-3 bg-stone-50 rounded-xl">
                <p className="text-xs font-medium text-stone-500 mb-1">Full session notes (not shown to parents)</p>
                <p className="text-sm text-stone-700 line-clamp-3">{note.objective || note.observation}</p>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </Card>
  );
}
